'use client';
import React from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '@/store';
/**
 * Custom hook for accessing authentication state
 * @returns Authentication state and utilities
 */
import { deriveSafe } from '@/utils/polymarket.utils';

/**
 * Custom hook for accessing authentication state
 * @returns Authentication state and utilities
 */
export const useAuth = () => {
  const authState = useSelector((state: RootState) => state.auth);

  const user = authState.user;
  const isWalletDeployed = !!user?.walletAddress;

  // Speculative Safe address only when not on the deposit-wallet CLOB path
  // (deriveSafe is wrong for Polymarket deposit-wallet funders).
  const shouldDeriveSafeFallback = user?.tradingWalletKind !== 'deposit_wallet';
  const effectiveProxy =
    user?.proxyWalletAddress ||
    (shouldDeriveSafeFallback && user?.walletAddress ? deriveSafe(user.walletAddress) : undefined);
  const isProxyDeployed = !!effectiveProxy;

  const hasClobCreds = !!user?.clobCredentials;

  // The user is ready to trade if we have an EOA and a Proxy (even if speculative)
  // hasClobCreds is strictly required to signed orders.
  const isReadyToTrade =
    isWalletDeployed && isProxyDeployed && hasClobCreds && authState.isReadyToTrade;

  const mergedUser = React.useMemo(() => {
    if (!authState.user) return null;
    return {
      ...authState.user,
      proxyWalletAddress: effectiveProxy || authState.user.proxyWalletAddress,
    };
  }, [authState.user, effectiveProxy]);

  return {
    isAuthenticated: authState.isAuthenticated,
    user: mergedUser,
    accessToken: authState.accessToken,
    isLoading: authState.isLoading,
    error: authState.error,
    isProxyInitializing: authState.isProxyInitializing,
    isSetupRequested: authState.isSetupRequested,
    isReadyToTrade,
    proxyError: authState.proxyError,
  };
};
